<template>
  <Teleport to="body">
    <BasePopup
      v-model="isOpen"
      max-width="560px"
      full-height-on-mobile
    >
      <template #title>
        {{ t('СВЯЗЬ С РАЗРАБОТЧИКАМИ') }}
      </template>
      <template #body>
        <div class="auth-contact-developers-popup__body-inner">
          <div class="auth-contact-developers-popup__intro-wrap">
            <BaseTypography
              type="text-secondary"
              text-align="center"
              color="var(--color-foreground-main)"
              width="100%"
            >
              {{ t('Напишите нам — мы поможем разобраться. При возможности прикрепите скриншот или запись экрана, чтобы ускорить решение.') }}
            </BaseTypography>
          </div>
          <BaseForm
            v-model="formModel"
            :schema="schema"
            :when-handle-submit="handleSubmit"
          >
            <template #component-attachment="{ attrs, handlers }">
              <BaseInputUploader
                v-bind="attrs"
                :label="t('Фото/Видео')"
                v-on="handlers"
              />
            </template>
            <template #actions="{ nextTabindex, loading }">
              <BaseButton
                type="submit"
                with-full
                with-decor
                variant="primary"
                data-testid="auth-dev-contact-submit-btn"
                :tabindex="nextTabindex"
                :loading="loading"
              >
                {{ t('Отправить') }}
              </BaseButton>
            </template>
          </BaseForm>
        </div>
      </template>
    </BasePopup>
  </Teleport>
</template>

<script setup lang="ts">
import type { ContactRequestMediaAttachmentInput } from '~/shared/api'
import type { SchemaType } from '~/shared/lib'

import { useI18n } from 'vue-i18n'
import { useSubmitContactRequestMutation } from '~/shared/api'
import { FormField } from '~/shared/const'
import { getUniqId, useNotificationStore } from '~/shared/lib'
import {
  BaseButton,
  BaseForm,
  BaseInputUploader,
  BasePopup,
  BaseTypography,
} from '~/shared/ui'
import {
  CONTACT_REQUEST_MAX_FILE_SIZE_BYTES,
  CONTACT_REQUEST_MAX_FILES,
} from '../model/const'
import { useContactRequestMediaUpload } from '../model/useContactRequestMediaUpload'
import i18n from './AuthContactDevelopersPopup.i18n.json'

const props = defineProps<{
  senderEmail?: string | null
}>()

const isOpen = defineModel<boolean>({ default: false })

const hasPrefilledSenderEmail = computed(() => Boolean(props.senderEmail?.trim()))

const { t } = useI18n(i18n)
const { notify } = useNotificationStore()
const { mutate: submitContactRequest } = useSubmitContactRequestMutation()
const { uploadContactRequestAttachments } = useContactRequestMediaUpload()

const formModel = ref({
  senderEmail: '',
  subject: '',
  description: '',
  attachment: [] as File[],
})

type ContactFormSchemaName = 'subject' | 'description' | 'senderEmail' | 'attachment'

const schema = computed((): Array<SchemaType<ContactFormSchemaName>> => {
  const senderEmailField: SchemaType<'senderEmail'> = {
    type: FormField.email,
    name: 'senderEmail',
    label: t('Email для ответа'),
    attrs: {
      'placeholder': t('Email для ответа'),
      'data-testid': 'auth-dev-contact-sender-email',
    },
    required: true,
  }

  return [
    ...(hasPrefilledSenderEmail.value ? [] : [senderEmailField]),
    {
      type: FormField.string,
      name: 'subject',
      label: t('Тема'),
      attrs: {
        'placeholder': t('Тема'),
        'data-testid': 'auth-dev-contact-subject',
      },
      required: true,
    },
    {
      type: FormField.textarea,
      name: 'description',
      label: t('Описание'),
      attrs: {
        'placeholder': t('Заголовок'),
        'rows': 5,
        'data-testid': 'auth-dev-contact-description',
      },
      required: true,
    },
    {
      type: FormField.fileUploader,
      name: 'attachment',
      label: t('Фото/Видео'),
      labelIsVisible: false,
      attrs: {
        'multiple': true,
        'maxFiles': CONTACT_REQUEST_MAX_FILES,
        'maxSize': CONTACT_REQUEST_MAX_FILE_SIZE_BYTES,
        'data-testid': 'auth-dev-contact-file-uploader',
      },
      required: false,
    },
  ] as Array<SchemaType<ContactFormSchemaName>>
})

watch(isOpen, (open) => {
  if (!open) {
    formModel.value = {
      senderEmail: '',
      subject: '',
      description: '',
      attachment: [],
    }
    return
  }

  formModel.value = {
    senderEmail: props.senderEmail?.trim() ?? '',
    subject: '',
    description: '',
    attachment: [],
  }
})

watch(
  () => props.senderEmail,
  (email) => {
    if (!isOpen.value) {
      return
    }
    const next = email?.trim() ?? ''
    if (next) {
      formModel.value.senderEmail = next
    }
  },
)

function normalizeAttachmentFiles(raw: unknown): File[] {
  if (!raw || !Array.isArray(raw)) {
    return []
  }
  return raw.filter((item): item is File => item instanceof File)
}

async function handleSubmit(data: {
  subject: string
  description: string
  senderEmail?: string
  attachment: File[] | unknown
}) {
  const subject = data.subject.trim()
  const description = data.description.trim()
  const senderEmail = (hasPrefilledSenderEmail.value
    ? props.senderEmail?.trim()
    : data.senderEmail?.trim()) ?? ''
  const files = normalizeAttachmentFiles(data.attachment)

  try {
    let mediaAttachments: ContactRequestMediaAttachmentInput[] | undefined

    if (files.length > 0) {
      const mediaEntityId = getUniqId()
      mediaAttachments = await uploadContactRequestAttachments(files, mediaEntityId)
    }

    const result = await submitContactRequest({
      input: {
        subject,
        description,
        senderEmail,
        ...(mediaAttachments?.length ? { mediaAttachments } : {}),
      },
    })

    if (result?.errors?.length) {
      notify({
        message: result.errors.map(e => e.message).join(', ') || t('Не удалось отправить обращение'),
        type: 'error',
      })
      return
    }

    if (!result?.data?.submitContactRequest) {
      notify({
        message: t('Не удалось отправить обращение'),
        type: 'error',
      })
      return
    }

    notify({
      message: t('Обращение отправлено'),
      type: 'success',
    })
    isOpen.value = false
  }
  catch (error) {
    notify({
      message: error instanceof Error ? error.message : t('Не удалось отправить обращение'),
      type: 'error',
    })
  }
}
</script>

<style scoped lang="scss">
.auth-contact-developers-popup {
  &__body-inner {
    width: 100%;
    text-align: left;
  }

  &__intro-wrap {
    margin: 0 auto rem(20px);
    max-width: rem(440px);
  }
}
</style>
